/**
 * RoleAdmin — founder-only · assign Phase-1 OS roles to admin users.
 *
 * Roles:
 *   founder      sees everything
 *   desk_agent   Operate + Module queues, cannot publish global content
 *   read_only    view only
 */
import React, { useEffect, useState, useContext } from "react";
import { api, formatApiError } from "@/lib/api";
import { OSContext } from "@/pages/admin/os/ConciergeOSShell";

const ROLE_OPTIONS = [
  { value: "founder", label: "Founder · sees everything" },
  { value: "desk_agent", label: "Desk Agent · queues + actions, cannot publish" },
  { value: "read_only", label: "Read-only · view only" },
  { value: "", label: "— · remove OS role" },
];

export default function RoleAdmin() {
  const { me } = useContext(OSContext);
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/os-admin/users");
      setUsers(data.users);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isFounder = me?.os_role === "founder";

  if (me && !isFounder) {
    return (
      <div data-testid="os-role-admin-locked">
        <div className="os-pagehead"><h1>Role Admin</h1></div>
        <div className="os-placeholder">
          <span className="tag">Founder only</span>
          <h2>Role assignment is restricted</h2>
          <p>
            Only the Founder role can assign or remove OS Admin roles.
            Your current role is <code style={{ color: "var(--os-brass-bright)" }}>{me?.os_role || "none"}</code>.
          </p>
        </div>
      </div>
    );
  }

  const setRole = async (email, role) => {
    setSaving(email);
    setMsg("");
    setErr("");
    try {
      await api.post("/os-admin/users/role", { email, os_role: role });
      setMsg(`Updated ${email} → ${role || "no OS role"}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving("");
    }
  };

  return (
    <div data-testid="os-role-admin">
      <div className="os-pagehead">
        <h1>Role Admin</h1>
        <span className="count">{users ? `${users.length} users` : "loading…"}</span>
      </div>
      <div className="os-pagesub">
        Phase-1 roles only — founder · desk_agent · read_only. Enforced in backend on every privileged endpoint.
      </div>

      {(err || msg) && (
        <div
          style={{
            background: err ? "rgba(199,106,91,0.08)" : "rgba(127,178,138,0.08)",
            border: `1px solid ${err ? "rgba(199,106,91,0.4)" : "rgba(127,178,138,0.4)"}`,
            color: err ? "#E5B6AE" : "#B3CFBA",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 12,
            fontSize: 12.5,
          }}
          data-testid="os-role-admin-msg"
        >
          {err || msg}
        </div>
      )}

      <table className="os-mig-table" data-testid="os-role-admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Current roles</th>
            <th>OS role</th>
            <th>Set OS role</th>
          </tr>
        </thead>
        <tbody>
          {users?.map((u) => (
            <tr key={u.email}>
              <td className="os-ref">{u.email}</td>
              <td style={{ color: "var(--os-muted)", fontSize: 11.5 }}>
                {(u.roles || []).join(" · ") || "—"}
              </td>
              <td>
                <span className={`os-pill ${u.os_role === "founder" ? "os-p-done" : u.os_role === "desk_agent" ? "os-p-prog" : u.os_role === "read_only" ? "os-p-new" : ""}`}>
                  {u.os_role || "none"}
                </span>
              </td>
              <td>
                <select
                  defaultValue={u.os_role || ""}
                  onChange={(e) => setRole(u.email, e.target.value)}
                  disabled={saving === u.email}
                  data-testid={`os-role-select-${u.email}`}
                  style={{
                    background: "var(--os-panel-2)",
                    color: "var(--os-ivory)",
                    border: "1px solid var(--os-hair)",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontFamily: "inherit",
                    fontSize: 12,
                  }}
                >
                  {ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
